/**
 * #1124 part B1 — trader-facing names for the views that ask the feed for
 * books and markets. View and source ids are grouped by the part before `:`
 * (a game key or event key follows it), so every Game Detail section is one
 * "Game Detail". An id with no name here is shown as-is, never dropped.
 */
import type { FeedNeedsSummary } from '@/stores/feedNeeds';
import { foldGameKeySport, promoteToGameKeyV2 } from '@/services/analyticsGameKey';

const NAMES: Record<string, string> = {
  // Game Detail: its sections, the trade panel in its rail, and the page's own route needs.
  'game-lines': 'Game Detail',
  'game-detail-moneyline': 'Game Detail',
  'game-detail-spread-total': 'Game Detail',
  'detail-panel': 'Game Detail',
  'game-detail': 'Game Detail',
  'game-sources': 'Game Detail',
  'position-tracker': 'Positions',
  'position-tracker-poly': 'Positions',
  'edit-position-modal': 'Edit position',
  'value-dashboard': 'Dashboard',
  'spreads-totals': 'Spreads/Totals',
  'golf-match-board': 'Golf boards',
  'g3-game-viewer': 'G3',
  'g3-realtime': 'G3',
  g3: 'G3',
  g9: 'G9',
  streamrift: 'StreamRift',
  live: 'Live',
  harry: 'Harry',
  'vanilla-mm': 'Vanilla MM',
  'prop-workbench-live': 'Prop workbench',
  'my-trades': 'My trades',
  'trader-detail': 'Trader detail',
  'org-detail': 'Org detail',
  'org-traders-table': 'Org traders',
  'account-plane': 'Your positions',
  'orderbook-rows': 'Rows for open books',
  route: 'This page',
};

export function viewGroupOf(id: string): string {
  const colon = id.indexOf(':');
  const base = colon >= 0 ? id.slice(0, colon) : id;
  return NAMES[base] ?? base;
}

export interface MarketViewGroup {
  label: string;
  /** `'all'` when any member asks for every sport; empty for a ticker-scoped view. */
  sports: 'all' | readonly string[];
  /** Distinct games named across the group; null when a member is broad on games. */
  games: number | null;
  /** Distinct tickers across the group; null when a member is ticker-broad. */
  tickers: number | null;
}

/** One game's identity across its wire-key forms: legacy or v2, a sport alias
 *  (`ncaaf`/`cfb`), or a split sport's ALT key. Game Detail declares the same
 *  game from two or three sources, sometimes in different forms. */
function gameIdentity(key: string): string {
  return foldGameKeySport(promoteToGameKeyV2(key.replace(/^([^|]+?)alt\|/i, '$1|'))).toLowerCase();
}

function union<T>(into: Set<T> | null, from: readonly T[] | null, map: (x: T) => T): Set<T> | null {
  if (into == null || from == null) return null;
  for (const x of from) into.add(map(x));
  return into;
}

/** Display order: a view naming a game first (it's the page's own), then by
 *  sports breadth, ticker-scoped views last. Stable for ties. */
function rank(g: MarketViewGroup): number {
  if (g.games != null && g.games > 0) return 0;
  if (g.sports === 'all') return 1;
  return g.sports.length > 0 ? 2 : 3;
}

/** Group the registry's per-source summaries by view name. */
export function groupMarketViews(summaries: readonly FeedNeedsSummary[]): MarketViewGroup[] {
  const same = (x: string) => x;
  const groups = new Map<string, { sports: 'all' | Set<string>; games: Set<string> | null; tickers: Set<string> | null }>();
  for (const s of summaries) {
    const label = viewGroupOf(s.sourceId);
    let g = groups.get(label);
    if (!g) {
      g = { sports: s.sports === 'all' ? 'all' : new Set(), games: new Set(), tickers: new Set() };
      groups.set(label, g);
    }
    if (g.sports !== 'all') {
      if (s.sports === 'all') g.sports = 'all';
      else for (const x of s.sports) g.sports.add(x);
    }
    g.games = union(g.games, s.games, gameIdentity);
    g.tickers = union(g.tickers, s.tickers, same);
  }
  const out = [...groups].map(([label, g]) => ({
    label,
    sports: g.sports === 'all' ? 'all' as const : [...g.sports].sort(),
    games: g.games?.size ?? null,
    tickers: g.tickers?.size ?? null,
  }));
  return out.map((g, i) => ({ g, i })).sort((a, b) => rank(a.g) - rank(b.g) || a.i - b.i).map(({ g }) => g);
}
